import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { obtenerCitasDoctor, obtenerCitas } from '../../api/citaService';
import { AuthContext } from '../../context/AuthContext';
import NuevaCitaModal from './NuevaCitaModal';
import GlobalDataTable from '../common/GlobalDataTable';
import GlobalSearchInput from '../common/GlobalSearchInput';
import { matchesSearchTerm } from '../../utils/globalSearch';

const FETCH_BATCH_SIZE = 100;

const CitasDoctor = () => {
  const { currentUser } = useContext(AuthContext);
  const [citas, setCitas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroFecha, setFiltroFecha] = useState('todas');
  const [filtroPaciente, setFiltroPaciente] = useState('');
  const [showNuevaCita, setShowNuevaCita] = useState(false);

  // Obtener parámetros de la URL para filtrar por paciente específico
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const pacienteParam = queryParams.get('paciente');
  
  useEffect(() => {
    // Si hay un paciente especificado en la URL, establecerlo como filtro
    if (pacienteParam) {
      setFiltroPaciente(pacienteParam);
    }
  }, [pacienteParam]);
  
  const cargarCitas = useCallback(async () => {
    try {
      setIsLoading(true);
      let response;
      
      // Verificar si el usuario es un doctor o un admin
      if (currentUser?.rol === 'DOCTOR') {
        response = await obtenerCitasDoctor({ page: 1, limit: FETCH_BATCH_SIZE });
      } else if (currentUser?.doctor?.id) {
        response = await obtenerCitas({ doctorId: currentUser.doctor.id, page: 1, limit: FETCH_BATCH_SIZE });
      } else {
        response = await obtenerCitas({ doctorId: currentUser?.id, page: 1, limit: FETCH_BATCH_SIZE });
      }

      const extraerCitas = (payload) => {
        if (payload?.data?.data?.citas) return payload.data.data.citas;
        if (payload?.data?.citas) return payload.data.citas;
        if (Array.isArray(payload?.data)) return payload.data;
        return [];
      };

      const cargarPagina = async (page) => {
        if (currentUser?.rol === 'DOCTOR') {
          return obtenerCitasDoctor({ page, limit: FETCH_BATCH_SIZE });
        }

        if (currentUser?.doctor?.id) {
          return obtenerCitas({ doctorId: currentUser.doctor.id, page, limit: FETCH_BATCH_SIZE });
        }

        return obtenerCitas({ doctorId: currentUser?.id, page, limit: FETCH_BATCH_SIZE });
      };

      if (response?.data) {
        const meta = response?.data?.data?.meta || response?.data?.meta || null;
        const totalPages = Math.max(1, meta?.pages || 1);

        const paginas = [extraerCitas(response)];

        for (let page = 2; page <= totalPages; page += 1) {
          const responsePage = await cargarPagina(page);
          paginas.push(extraerCitas(responsePage));
        }

        const citasData = paginas.flat();

        setCitas(citasData.map(cita => ({
          ...cita,
          fecha: new Date(cita.fecha),
          // Asegurar que todos los campos necesarios existan
          servicio: cita.servicio || { nombre: 'Sin especificar' },
          estado: (cita.estado || 'pendiente').toLowerCase(),
          paciente: cita.paciente || { nombre: 'Sin nombre', apellido: '' }
        })));
      } else {
        console.error('Formato de respuesta inesperado:', response);
        setCitas([]);
      }
      setError(null);
    } catch (err) {
      console.error('Error al cargar citas:', err);
      setError('No se pudieron cargar las citas. Por favor, intente de nuevo más tarde.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      cargarCitas();
    }
  }, [currentUser, cargarCitas]);

  useEffect(() => {
    const handleDashboardAction = (event) => {
      if (event?.detail?.action === 'nueva-cita') {
        setShowNuevaCita(true);
      }
    };

    window.addEventListener('dashboard-action', handleDashboardAction);
    return () => window.removeEventListener('dashboard-action', handleDashboardAction);
  }, []);
  
  // Función para aplicar los filtros
  const citasFiltradas = citas.filter(cita => {
    try {
      // Verificar que cita tenga campos obligatorios
      if (!cita || !cita.fecha || !cita.paciente) {
        return false;
      }

      // Filtro por fecha
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      if (filtroFecha === 'hoy') {
        const citaDate = new Date(cita.fecha);
        if (!(citaDate.getDate() === today.getDate() && 
              citaDate.getMonth() === today.getMonth() && 
              citaDate.getFullYear() === today.getFullYear())) {
          return false;
        }
      } else if (filtroFecha === 'mañana') {
        const citaDate = new Date(cita.fecha);
        if (!(citaDate.getDate() === tomorrow.getDate() && 
              citaDate.getMonth() === tomorrow.getMonth() && 
              citaDate.getFullYear() === tomorrow.getFullYear())) {
          return false;
        }
      } else if (filtroFecha === 'semana') {
        const nextWeek = new Date(today);
        nextWeek.setDate(today.getDate() + 7);
        const citaDate = new Date(cita.fecha);
        if (!(citaDate >= today && citaDate <= nextWeek)) {
          return false;
        }
      }
      
      // Filtro por paciente
      if (filtroPaciente && cita.paciente) {
        const nombre = cita.paciente.nombre || '';
        const apellido = cita.paciente.apellido || '';
        const cedula = cita.paciente.cedula || '';
        const email = cita.paciente.email || '';

        if (!matchesSearchTerm(filtroPaciente, `${nombre} ${apellido}`, cedula, email)) {
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error al filtrar cita:', error, cita);
      return false;
    }
  });
  
  // Ordenar citas por fecha (más cercanas primero)
  const citasOrdenadas = [...citasFiltradas].sort((a, b) => {
    // Asegurar que ambas fechas son objetos Date válidos
    const fechaA = a.fecha instanceof Date ? a.fecha : new Date(a.fecha);
    const fechaB = b.fecha instanceof Date ? b.fecha : new Date(b.fecha);
    return fechaA - fechaB;
  });
  
  // Función para obtener la clase de estado
  const getEstadoClass = (estado) => {
    switch (estado) {
      case 'confirmada': return { bg: '#e8f5e9', color: '#4caf50' };
      case 'pendiente': return { bg: '#fff3e0', color: '#ff9800' };
      case 'completada': return { bg: '#e1f5fe', color: '#03a9f4' };
      case 'cancelada': return { bg: '#ffebee', color: '#f44336' };
      default: return { bg: '#f5f5f5', color: '#757575' };
    }
  };

  const formatNumeroCita = (numero) => {
    if (typeof numero !== 'number') return '';
    return `C-${String(numero).padStart(4, '0')}`;
  };

  const columnasCitasDoctor = [
    {
      key: 'paciente',
      header: 'Paciente',
      render: (cita) => (
        <div>
          <div style={{ fontWeight: 700 }}>
            {cita.paciente?.nombre || ''} {cita.paciente?.apellido || ''}
          </div>
          <div className="global-table-subtext">{cita.paciente?.cedula || 'Sin cédula'}</div>
        </div>
      ),
    },
    {
      key: 'servicio',
      header: 'Servicio',
      render: (cita) => cita.servicio?.nombre || 'Servicio sin nombre',
    },
    {
      key: 'fechaHora',
      header: 'Fecha y Hora',
      render: (cita) => (
        <div>
          <div>
            {cita.fecha.toLocaleDateString('es-ES', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            })}
          </div>
          <div className="global-table-subtext">
            {cita.horaInicio || cita.fecha.toLocaleTimeString('es-ES', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        </div>
      ),
    },
    {
      key: 'estado',
      header: 'Estado',
      render: (cita) => {
        const estadoClass = getEstadoClass(cita.estado);
        return (
          <span className="global-badge" style={{ backgroundColor: estadoClass.bg, color: estadoClass.color }}>
            {(cita.estado?.charAt(0).toUpperCase() + cita.estado?.slice(1)) || 'Pendiente'}
          </span>
        );
      },
    },
    {
      key: 'contacto',
      header: 'Contacto',
      render: (cita) => (
        <div>
          <div>
            <i className="fas fa-phone" style={{ marginRight: '5px', color: '#666' }}></i>
            {cita.paciente?.telefono || 'Sin teléfono'}
          </div>
          <div className="global-table-subtext">
            <i className="fas fa-envelope" style={{ marginRight: '5px', color: '#666' }}></i>
            {cita.paciente?.email || 'Sin email'}
          </div>
        </div>
      ),
    },
    {
      key: 'acciones',
      header: 'Acciones',
      align: 'center',
      render: (cita) => (
        <div className="global-table-actions">
          <Link
            to={`/dashboard/citas/${cita.id}`}
            state={{ citaNumero: formatNumeroCita(cita.numero) }}
            className="global-table-btn global-table-btn--info"
          >
            Ver detalles
          </Link>

          {cita.paciente?.cedula && (
            <Link to={`/dashboard/historia-clinica/${cita.paciente.cedula}`} className="global-table-btn global-table-btn--primary">
              Historia
            </Link>
          )}
        </div>
      ),
    },
  ];

  const handleCitaCreada = async () => {
    setShowNuevaCita(false);
    await cargarCitas();
  };
  
  return (
    <div style={{ padding: '20px' }}>
      <NuevaCitaModal
        isOpen={showNuevaCita}
        onClose={() => setShowNuevaCita(false)}
        onCreated={handleCitaCreada}
      />      
      {/* Filtros */}
      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: '20px', 
        marginBottom: '30px',
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#666' }}>
            Filtrar por fecha:
          </label>
          <select 
            value={filtroFecha} 
            onChange={(e) => setFiltroFecha(e.target.value)}
            style={{
              padding: '10px 15px',
              borderRadius: '5px',
              border: '1px solid #ddd',
              width: '180px'
            }}
          >
            <option value="todas">Todas las fechas</option>
            <option value="hoy">Hoy</option>
            <option value="mañana">Mañana</option>
            <option value="semana">Próxima semana</option>
          </select>
        </div>
        
        <div style={{ flex: 1, minWidth: '250px', maxWidth: '370px' }}>
          <GlobalSearchInput
            label="Buscar paciente:"
            value={filtroPaciente}
            onChange={(e) => setFiltroPaciente(e.target.value)}
            placeholder="Nombre, cédula o email"
          />
        </div>
      </div>
      
      {/* Estado de carga */}
      {isLoading && (
        <div style={{ 
          padding: '30px', 
          textAlign: 'center', 
          backgroundColor: 'white',
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <p>Cargando citas...</p>
        </div>
      )}
      
      {/* Mensaje de error */}
      {error && (
        <div style={{ 
          padding: '30px', 
          textAlign: 'center', 
          backgroundColor: 'white',
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          color: '#f44336'
        }}>
          <p>{error}</p>
        </div>
      )}
      
      {/* Mensaje si no hay citas aún estando conectado */}
      {!isLoading && !error && citas.length === 0 && (
        <div style={{ 
          padding: '30px', 
          textAlign: 'center', 
          backgroundColor: 'white',
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          color: '#666'
        }}>
          <p>No se encontraron citas registradas.</p>
        </div>
      )}
      
      {/* Lista de citas */}
      {!isLoading && !error && (
        <GlobalDataTable
          columns={columnasCitasDoctor}
          rows={citasOrdenadas}
          rowKey="id"
          emptyMessage="No se encontraron citas que coincidan con los filtros seleccionados"
          emptyIcon={<i className="fas fa-calendar-times"></i>}
          rowsPerPage={10}
          pageResetKey={`${filtroPaciente}|${filtroFecha}`}
        />
      )}
    </div>
  );
};

export default CitasDoctor;